import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  makeStyles,
  TextField,
  Typography
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import PersonIcon from '@material-ui/icons/Person';
import { useDispatch } from 'react-redux';
import axiosInterceptor from 'src/app/axiosInterceptor';
import { messageToastType_const } from 'src/app/constant/config';
import { showGlobalToast } from 'src/features/uiSlice';

const useStyles = makeStyles(() => ({
  dialogPaper: {
    borderRadius: 14,
    overflow: 'hidden',
    width: '100%',
    maxWidth: 440,
    boxShadow:
      '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    backgroundColor: '#fff',
    borderBottom: '1px solid #f1f5f9'
  },
  headerIcon: {
    width: 38,
    height: 38,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  headerIconLogout: {
    backgroundColor: '#fef2f2',
    color: '#dc2626'
  },
  headerIconLogin: {
    backgroundColor: '#fde8ec',
    color: '#d9042f'
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#0f172a',
    lineHeight: 1.2
  },
  headerSubtitle: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 2
  },
  closeBtn: {
    color: '#94a3b8',
    padding: 6,
    borderRadius: 8,
    '&:hover': {
      backgroundColor: '#f1f5f9',
      color: '#334155'
    }
  },
  dialogContent: {
    padding: '18px 20px',
    backgroundColor: '#fff'
  },
  deviceBadge: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 14px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    marginBottom: 16
  },
  badgeLabel: {
    color: '#64748b',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.3
  },
  badgeValue: {
    fontWeight: 700,
    color: '#0f172a',
    fontSize: 14,
    marginTop: 2
  },
  formGroup: {
    marginBottom: 16,
    '&:last-child': {
      marginBottom: 0
    }
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: '#334155',
    marginBottom: 6
  },
  requiredStar: {
    color: '#dc2626'
  },
  inputField: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: '#fff !important',
      transition: 'border-color 0.15s ease',
      '& fieldset': {
        borderColor: '#e2e8f0 !important'
      },
      '&:hover fieldset': {
        borderColor: '#cbd5e1 !important'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#d9042f !important',
        borderWidth: 1.5
      }
    },
    '& .MuiOutlinedInput-notchedOutline legend': {
      display: 'none !important'
    },
    '& .MuiInputBase-input': {
      fontSize: 14,
      padding: '10px 14px',
      color: '#1e293b',
      backgroundColor: 'transparent !important'
    }
  },
  inputFieldReadOnly: {
    '& .MuiOutlinedInput-root': {
      cursor: 'not-allowed !important',
      backgroundColor: '#fff !important',
      '&:hover fieldset': {
        borderColor: '#e2e8f0 !important'
      }
    },
    '& .MuiInputBase-input': {
      cursor: 'not-allowed !important',
      color: '#1e293b !important',
      fontWeight: 500,
      backgroundColor: 'transparent !important'
    }
  },
  dialogActions: {
    padding: '14px 20px',
    borderTop: '1px solid #f1f5f9',
    backgroundColor: '#fafbfc',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8
  },
  cancelBtn: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: 13,
    color: '#64748b',
    borderRadius: 8,
    padding: '7px 16px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#fff',
    '&:hover': {
      backgroundColor: '#f8fafc',
      borderColor: '#cbd5e1'
    }
  },
  submitBtn: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: 13,
    backgroundColor: '#d9042f',
    color: '#fff',
    borderRadius: 8,
    padding: '7px 18px',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#b90327',
      boxShadow: 'none'
    }
  },
  logoutSubmitBtn: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: 13,
    backgroundColor: '#dc2626',
    color: '#fff',
    borderRadius: 8,
    padding: '7px 18px',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#b91c1c',
      boxShadow: 'none'
    }
  }
}));

const DriverLoginModal = ({
  open,
  mode = 'login',
  onClose,
  vehicle,
  onSuccess
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const isLogout = mode === 'logout';
  const [driverName, setDriverName] = useState('');
  const [driverLicense, setDriverLicense] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      if (isLogout) {
        const curName =
          vehicle?.driver_name && vehicle.driver_name !== 'Lái xe đăng xuất'
            ? vehicle.driver_name
            : '';
        const curLicense =
          vehicle?.driver_license &&
          vehicle.driver_license !== 'VGPS' &&
          vehicle.driver_license !== '--'
            ? vehicle.driver_license
            : '';
        setDriverName(curName);
        setDriverLicense(curLicense);
      } else {
        setDriverName('');
        setDriverLicense('');
      }
    }
  }, [open, isLogout, vehicle]);

  const deviceId = vehicle?.device_id || vehicle?.id;

  const handleSubmit = async e => {
    if (e) e.preventDefault();
    if (!deviceId) {
      dispatch(
        showGlobalToast({
          type: messageToastType_const.error,
          message: 'Không tìm thấy ID thiết bị (deviceId)'
        })
      );
      return;
    }
    if (!driverName.trim() || !driverLicense.trim()) {
      dispatch(
        showGlobalToast({
          type: messageToastType_const.error,
          message: 'Vui lòng nhập đầy đủ tên lái xe và số bằng lái'
        })
      );
      return;
    }

    setLoading(true);
    try {
      const payload = {
        driver_name: driverName.trim(),
        driver_license: driverLicense.trim(),
        type: isLogout ? 'logout' : 'login'
      };
      const res = await axiosInterceptor.post(
        `/vehicles/mqtt/driver-login/${deviceId}`,
        payload
      );
      const actionText = isLogout ? 'Đăng xuất' : 'Đăng nhập';
      dispatch(
        showGlobalToast({
          type: messageToastType_const.success,
          message:
            res.data?.message ||
            `${actionText} lái xe thành công (${payload.driver_name})`
        })
      );
      if (onSuccess) {
        onSuccess(payload);
      }
      onClose();
    } catch (err) {
      const rawMsg =
        err?.response?.data?.message || err?.data?.message || err?.message || '';
      let errMsg = isLogout
        ? 'Đăng xuất lái xe thất bại'
        : 'Đăng nhập lái xe thất bại';
      if (rawMsg.includes('Device location not found')) {
        errMsg =
          'Thiết bị chưa có vị trí toạ độ. Vui lòng thử lại khi thiết bị online.';
      } else if (rawMsg.includes('No active login session')) {
        errMsg = 'Thiết bị chưa có phiên đăng nhập hoạt động.';
      } else if (rawMsg.includes('Vehicle not found')) {
        errMsg = 'Không tìm thấy thông tin thiết bị (deviceId).';
      } else if (rawMsg) {
        errMsg = rawMsg;
      }

      dispatch(
        showGlobalToast({
          type: messageToastType_const.error,
          message: errMsg
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      classes={{ paper: classes.dialogPaper }}
      maxWidth="xs"
      fullWidth
    >
      <Box className={classes.dialogHeader}>
        <Box display="flex" alignItems="center" style={{ gap: 12 }}>
          <Box
            className={`${classes.headerIcon} ${
              isLogout ? classes.headerIconLogout : classes.headerIconLogin
            }`}
          >
            {isLogout ? (
              <ExitToAppIcon fontSize="small" />
            ) : (
              <PersonIcon fontSize="small" />
            )}
          </Box>
          <Box>
            <Typography className={classes.headerTitle}>
              {isLogout ? 'Đăng xuất lái xe' : 'Đăng nhập lái xe'}
            </Typography>
            <Typography className={classes.headerSubtitle}>
              {isLogout
                ? 'Xác nhận kết thúc ca làm việc của tài xế'
                : 'Gửi bản tin đăng nhập lái xe tới thiết bị'}
            </Typography>
          </Box>
        </Box>
        <IconButton
          size="small"
          className={classes.closeBtn}
          onClick={onClose}
          disabled={loading}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <form onSubmit={handleSubmit}>
        <DialogContent className={classes.dialogContent}>
          <Box className={classes.deviceBadge}>
            <Box>
              <div className={classes.badgeLabel}>Biển số / Serial</div>
              <div className={classes.badgeValue}>
                {vehicle?.license_plate || vehicle?.serial || '--'}
              </div>
            </Box>
            <Box textAlign="right">
              <div className={classes.badgeLabel}>Device ID</div>
              <div className={classes.badgeValue}>{deviceId || '--'}</div>
            </Box>
          </Box>

          <Box className={classes.formGroup}>
            <Typography className={classes.fieldLabel}>
              Tên lái xe {!isLogout && <span className={classes.requiredStar}>*</span>}
            </Typography>
            <TextField
              className={`${classes.inputField} ${
                isLogout ? classes.inputFieldReadOnly : ''
              }`}
              value={driverName}
              onChange={e => setDriverName(e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Nhập tên lái xe"
              disabled={loading}
              autoFocus={!isLogout}
              InputProps={{
                readOnly: isLogout,
                notched: false
              }}
            />
          </Box>

          <Box className={classes.formGroup}>
            <Typography className={classes.fieldLabel}>
              Số bằng lái {!isLogout && <span className={classes.requiredStar}>*</span>}
            </Typography>
            <TextField
              className={`${classes.inputField} ${
                isLogout ? classes.inputFieldReadOnly : ''
              }`}
              value={driverLicense}
              onChange={e => setDriverLicense(e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Nhập số bằng lái"
              disabled={loading}
              InputProps={{
                readOnly: isLogout,
                notched: false
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button
            className={classes.cancelBtn}
            onClick={onClose}
            disabled={loading}
          >
            Hủy
          </Button>
          <Button
            type="submit"
            variant="contained"
            className={isLogout ? classes.logoutSubmitBtn : classes.submitBtn}
            autoFocus={isLogout}
            disabled={
              loading || !driverName.trim() || !driverLicense.trim() || !deviceId
            }
            startIcon={
              loading ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
          >
            {loading ? 'Đang gửi...' : isLogout ? 'Đăng xuất' : 'Đăng nhập'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default DriverLoginModal;
